/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import logger from 'utils/logger';
import { reportErrorWithScope } from 'utils/sentry';
import CustomErrorPage from 'components/ErrorPages/CustomErrorPage';
import {
  clearReloadOnceGuard,
  reloadOnceForDynamicImportError,
} from 'utils/dynamicImportError';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export default class RootErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidMount() {
    // componentDidMount still fires after an error is caught during the
    // initial render, since the boundary's own fallback render is what
    // actually commits - guard on hasError so this only clears the guard
    // on a genuinely clean mount, not right after the reload we just
    // triggered from componentDidCatch (which would otherwise re-arm the
    // guard immediately and cause a reload loop on a persistent error).
    const { hasError } = this.state;
    if (!hasError) {
      clearReloadOnceGuard();
    }
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    // You can also log the error to an error reporting service
    // TODO tmkasun: Submit error to sentry
    logger.error('An error occurred:', error, JSON.stringify(errorInfo));
    reportErrorWithScope(error, { errorInfo });

    reloadOnceForDynamicImportError(error);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return <CustomErrorPage type="GenericError" />;
    }

    return children;
  }
}
